import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

/**
 * Session state.
 *
 * Replaces the Base44 version, which read an access token out of the URL query
 * string and kept it in localStorage. Supabase manages the session and refresh
 * itself; the profile (name and role) comes from our API, because role is what
 * decides whether the refund and delete buttons appear at all.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      setProfile(await api.me());
      setError(null);
    } catch (err) {
      setProfile(null);
      // A valid Supabase login with no profile row means the account exists but
      // has not been added to the shop, which is a different problem from being
      // signed out and needs a different message.
      setError(err.isForbidden ? err.message : 'Could not load your profile.');
    }
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session ?? null);
      if (data.session) await loadProfile();
      if (active) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, next) => {
      if (!active) return;
      setSession(next ?? null);
      if (next) {
        await loadProfile();
      } else {
        setProfile(null);
        setError(null);
      }
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async (email, password) => {
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) throw new Error(signInError.message);
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();

    // Drop anything the service worker cached for this session. The Base44
    // worker cached API responses to disk and never cleared them, so the next
    // person at a shared shop machine could read the previous user's data.
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: profile,
      isAuthenticated: Boolean(session && profile),
      isAdmin: profile?.role === 'admin',
      loading,
      error,
      signIn,
      signOut,
      reload: loadProfile,
    }),
    [session, profile, loading, error, signIn, signOut, loadProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
